import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  BarChart3, 
  Activity, 
  DollarSign, 
  RotateCcw, 
  TrendingUp, 
  Trophy 
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { usePropBotStats } from '@/hooks/use-prop-bot-stats';

export const PropBotStatsCards = () => {
  const { data: stats, isLoading, error } = usePropBotStats();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16 mb-2" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="col-span-full">
        <CardContent className="p-6">
          <p className="text-destructive">Failed to load prop bot statistics</p>
        </CardContent>
      </Card>
    );
  }

  const statCards = [
    {
      title: 'Total Prop Bots',
      value: `${stats?.activePropBots || 0}/${stats?.totalPropBots || 0}`,
      icon: BarChart3,
      description: 'Active/Total bots',
      color: 'text-blue-600'
    },
    {
      title: 'Connected Accounts',
      value: stats?.activeConnectedAccounts?.toLocaleString() || '0',
      icon: Activity,
      description: 'Active accounts',
      color: 'text-green-600'
    },
    {
      title: 'Total Capital',
      value: `$${(stats?.totalCapital || 0).toLocaleString()}`,
      icon: DollarSign,
      description: 'Under management',
      color: 'text-emerald-600'
    },
    {
      title: 'Reverse Positions',
      value: stats?.reversePositionAccounts?.toLocaleString() || '0',
      icon: RotateCcw,
      description: 'Shadow bot accounts',
      color: 'text-purple-600'
    },
    {
      title: 'Monthly Growth',
      value: `${stats?.growthRate || 0}%`,
      icon: TrendingUp,
      description: 'Last 30 days',
      color: stats?.growthRate && stats.growthRate > 0 ? 'text-green-600' : 'text-red-600'
    },
    {
      title: 'Top Bot',
      value: stats?.topPerformingBot?.accountCount?.toString() || '0',
      icon: Trophy,
      description: stats?.topPerformingBot?.name || 'No data',
      color: 'text-yellow-600'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
      {statCards.map((stat, index) => (
        <Card key={index} className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {stat.title}
            </CardTitle>
            <stat.icon className={`h-4 w-4 ${stat.color}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stat.value}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stat.description}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};